import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  OutlinedInput,
  Typography,
  Tooltip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ProfileImg from '../../../assets/images/profile/user-1.jpg';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import TextInputs from 'src/components/inputs/TextInputs';
import { useMain } from 'src/views/hooks/useMain';
import imageCompression from 'browser-image-compression';
import InputSelect from './CustomInputSelect';
import Template1Image from '../../../assets/Template-images/template1.png';
import Template2Image from '../../../assets/Template-images/template2.png';
import Template3Image from '../../../assets/Template-images/template3.png';
import ReactSelect from 'react-select';
import { ReqField } from 'src/components/CustomComponents/CustomLoader';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const GSTIN_POSITION_RULES = [
  [/\d/, 'First 2 characters must be digits'],
  [/\d/, 'First 2 characters must be digits'],
  [/[A-Z]/, 'Characters 3 to 7 must be letters'],
  [/[A-Z]/, 'Characters 3 to 7 must be letters'],
  [/[A-Z]/, 'Characters 3 to 7 must be letters'],
  [/[A-Z]/, 'Characters 3 to 7 must be letters'],
  [/[A-Z]/, 'Characters 3 to 7 must be letters'],
  [/\d/, 'Characters 8 to 11 must be digits'],
  [/\d/, 'Characters 8 to 11 must be digits'],
  [/\d/, 'Characters 8 to 11 must be digits'],
  [/\d/, 'Characters 8 to 11 must be digits'],
  [/[A-Z]/, 'Character 12 must be a letter'],
  [/[1-9A-Z]/, 'Character 13 must be 1-9 or a letter'],
  [/Z/, 'Character 14 must be the letter Z'],
  [/[A-Z0-9]/, 'Character 15 must be a letter or digit'],
];

const getGstFormatError = (value) => {
  if (!value || value.length < 4) return '';
  for (let i = 0; i < value.length && i < 15; i++) {
    const [rule, message] = GSTIN_POSITION_RULES[i];
    if (!rule.test(value[i])) return `Invalid GST: ${message}`;
  }
  if (value.length === 15 && !GSTIN_REGEX.test(value)) return 'Invalid GST number format';
  return '';
};

const getGstLengthError = (value) =>
  value && value.length < 15 ? `${15 - value.length} more character(s) needed` : '';
const useStyles = makeStyles({
  container: {
    padding: '20px',
    backgroundColor: '#FAFAFA',
    borderRadius: '10px',
    border: '0.5px solid #CFCFCF',
  },
});

//for normal time slot selector
// const CustomTimeSlotSelector = ({ title, name, value, handleChange, required, fieldValidation, startTime = 9 }) => {
//   const generateTimeOptions = () => {
//     const options = [];
//     for (let hour = startTime; hour < 24; hour++) {
//       for (let minute = 0; minute < 60; minute += 10) {
//         let hours12 = hour % 12 || 12;
//         let period = hour < 12 ? 'AM' : 'PM';
//         let time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
//         let label = `${String(hours12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
//         options.push({ value: time, label: label });
//       }
//     }
//     return options;
//   };
const CustomTimeSlotSelector = ({ title, name, value, handleChange, required, fieldValidation, startTime = 0, minTime }) => {
  const generateTimeOptions = () => {
    const options = [];
    const totalDayMinutes = 24 * 60;

    if (minTime) {
      // Closing selector: build the list going BACKWARD from opening time.
      // First option = 1 minute before opening. Then step back 10 minutes at a
      // time (…08:59, 08:50, 08:40 … for a 09:00 opening), wrapping past
      // midnight, covering the full day without landing back on opening time.
      const [openHour, openMinute] = minTime.split(':').map(Number);
      const openTotal = openHour * 60 + openMinute;

      // First slot: exactly 1 minute before opening.
      let firstTotal = (openTotal - 1 + totalDayMinutes) % totalDayMinutes;
      options.push(buildOption(firstTotal));

      // Then step backward in 10-minute increments from the nearest 10-min
      // mark below the first slot, for the rest of the day (143 more slots).
      let cur = Math.floor(firstTotal / 10) * 10;
      for (let i = 0; i < 142; i++) {
        cur = (cur - 10 + totalDayMinutes) % totalDayMinutes;
        options.push(buildOption(cur));
      }

      return options;
    }

    // Opening selector: unchanged — full 24 hours in natural forward order.
    for (let total = startTime * 60; total < startTime * 60 + totalDayMinutes; total += 10) {
      options.push(buildOption(total % totalDayMinutes));
    }
    return options;
  };

// Helper: minutes-since-midnight -> { value: "HH:MM", label: "hh:mm AM/PM" }
const buildOption = (totalMinutes) => {
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const hours12 = hour % 12 || 12;
  const period = hour < 12 ? 'AM' : 'PM';
  const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  const label = `${String(hours12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
  return { value: time, label: label };
};
  const timeOptions = generateTimeOptions();

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '3.3rem',
      fontSize: '1rem',
      borderColor: state.isFocused ? '#6174DD' : '#ccc',
      boxShadow: state.isFocused ? '0 0 0 1px #6174DD' : null,
      '&:hover': {
        borderColor: state.isFocused ? '#6174DD' : '#ccc'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '1rem'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#6174DD' : state.isFocused ? '#E0E7FF' : 'white',
      color: state.isSelected ? 'white' : 'black',
      '&:hover': {
        backgroundColor: '#6174DD',
        color: 'white'
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'black'
    })
  };

  const formatTimeTo24hr = (selectedTime) => {
    const [time, period] = selectedTime.split(' ');
    let [hours, minutes] = time.split(':');
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);

    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const handleChangeSelect = (selectedOption) => {
    const formattedTime = selectedOption ? formatTimeTo24hr(selectedOption.label) : '';
    handleChange({ target: { name, value: formattedTime } });
  };

  return (
    <div style={{ fontFamily: 'Arial, sans-serif' }}>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        component="label"
        htmlFor={name}
        mb="5px"
      >
        {title} {required !== false && <ReqField />}
      </Typography>
      <ReactSelect
        key={minTime || 'no-min'}
        options={timeOptions}
        value={timeOptions.find(option => option.value === value)}
        onChange={handleChangeSelect}
        isClearable
        styles={customStyles}
        maxMenuHeight={250}
        menuPlacement="auto"
      />
      {fieldValidation && (
        <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.25rem' }}>
          This field is required
        </div>
      )}
    </div>
  );
};


const templates = [
  { id: 1, name: 'Template for A4 Size Printer', image: Template2Image },
  { id: 2, name: 'Modern template with Review', image: Template3Image },
  { id: 3, name: 'Minimal Template For Thermal Printer ', image: Template1Image },
];

const initialUpdateData = {
  name: '',
  phone: '',
  address: '',
  gstNumber: '',
  confirmGstNumber: '',
  perRewardPointsPrice: 0,
  websiteLink: '',
  signature: null,
  selectedBillTemplate: 1,
  salonOpeningTime: '',
  salonClosingTime: '',
};

const OwnerProfile = ({ data, refreshData, selectedTemplate }) => {
  const { updateOwnerProfile, setShowMessage, updateOwnerProfileImage , verifyGstin , activeBranchId} = useMain();

  const [updateType, setUpdateType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDialogue, setOpenDialogue] = useState(false);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [logo, setLogo] = useState('');
  const [currentTemplate, setCurrentTemplate] = useState(selectedTemplate || 1);
  const [gstDetail, setGstDetail] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstError, setGstError] = useState('');
  const [gstTouched, setGstTouched] = useState(false);
  const [confirmGstError, setConfirmGstError] = useState('');
  const [openGstDialog, setOpenGstDialog] = useState(false);
  const [updateFormData, setUpdateFormData] = useState({
    ...initialUpdateData,
    selectedBillTemplate: selectedTemplate || 1
  });

  const classes = useStyles();
  console.log(selectedTemplate, 'selectedTemplate')
  useEffect(() => {
    setCurrentTemplate(selectedTemplate || 1);
    setUpdateFormData(prev => ({
      ...prev,
      selectedBillTemplate: selectedTemplate || 1
    }));
  }, [selectedTemplate]);

  useEffect(() => {
    // Initialize form data with the current selected template
    if (data) {
      setUpdateFormData(prev => ({
        ...prev,
        selectedBillTemplate: selectedTemplate || 1,
        name: data.name || '',
        phone: data.phone || '',
        address: data.address || '',
        gstNumber: data.gstNumber || '',
        confirmGstNumber: data.gstNumber || '',
        perRewardPointsPrice: data?.perRewardPointsPrice || 0,
        websiteLink: data?.websiteLink || '',
        salonOpeningTime: data?.salonOpeningTime || '',
        salonClosingTime: data?.salonClosingTime || '',
      }));
    }
  }, [data, selectedTemplate]);
  // Returns the list of valid closing-time values (10-min slots) given an opening time,
// wrapping past midnight — mirrors CustomTimeSlotSelector's generateTimeOptions logic.
const getValidClosingTimes = (openingTime) => {
  if (!openingTime) return [];
  const [h, m] = openingTime.split(':').map(Number);
  const totalSlots = 24 * 6;
  const startIndex = h * 6 + Math.floor(m / 10);
  const valid = [];
  for (let i = 1; i <= totalSlots; i++) {
    const slotIndex = (startIndex + i) % totalSlots;
    const hour = Math.floor(slotIndex / 6);
    const minute = (slotIndex % 6) * 10;
    valid.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
  }
  return valid;
};

useEffect(() => {
  setUpdateFormData((prev) => {
    if (!prev.salonOpeningTime || !prev.salonClosingTime) return prev;

    const validClosingTimes = getValidClosingTimes(prev.salonOpeningTime);
    if (!validClosingTimes.includes(prev.salonClosingTime)) {
      return { ...prev, salonClosingTime: '' };
    }
    return prev;
  });
}, [updateFormData.salonOpeningTime]);

  const fetchGstDetail = async (gstin) => {
    setGstLoading(true);
    setGstError('');
    try {
      const res = await verifyGstin(gstin, activeBranchId);
      if (res?.success && res?.data) {
        setGstDetail({ gstin, ...res.data });
        return true;
      }
      setGstDetail(null);
      setGstError(res?.message || 'Invalid GST number');
      return false;
    } catch (e) {
      setGstDetail(null);
      setGstError('Could not verify GST number');
      return false;
    } finally {
      setGstLoading(false);
    }
  };

  const handleOpenDialogue = (type) => {
    setOpenDialogue(true);
    setUpdateType(type);
  };
  const handleCloseDialogue = () => {
    setGstDetail(null);
    setGstError('');
    setGstTouched(false);
    setConfirmGstError('');
    setUpdateType(null);
    setLogo('');
    // setUpdateFormData(initialUpdateData);
    setOpenDialogue(false);
  };

  const handleTemplateSelect = (templateId) => {
    // Update both the form data immediately
    setUpdateFormData(prev => ({
      ...prev,
      selectedBillTemplate: templateId
    }));
  };

  // const handleChange = (e) => {
  //   const { name, value } = e.target;
  //   setUpdateFormData((prev) => ({ ...prev, [name]: value }));
  // };
    const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'gstNumber') {
      const v = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
      setUpdateFormData((prev) => ({ ...prev, gstNumber: v }));
      setGstDetail(null);

      const formatError = getGstFormatError(v);
      setGstError(formatError);
      setConfirmGstError(
        updateFormData.confirmGstNumber && updateFormData.confirmGstNumber !== v
          ? 'GST numbers do not match' : ''
      );

      if (v.length === 15 && !formatError) fetchGstDetail(v); // hits API only for a full valid number
      return;
    }

    if (name === 'confirmGstNumber') {
      const v = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
      setUpdateFormData((prev) => ({ ...prev, confirmGstNumber: v }));
            setConfirmGstError(v && v !== updateFormData.gstNumber ? 'GST numbers do not match' : '');
      return;
    }

    if (name === 'phone') {
      setUpdateFormData((prev) => ({ ...prev, phone: cleanPhone(value) }));
      return;
    }

    if (['name', 'address', 'websiteLink'].includes(name)) {
      setUpdateFormData((prev) => ({ ...prev, [name]: value.trimStart() }));
      return;
    }

    setUpdateFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Digits only, removes +91 / 91 country code, max 10 digits
  const cleanPhone = (raw) => {
    let digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) {
      digits = digits.slice(2);
    }
    return digits.slice(0, 10);
  };

  // Trims trailing spaces when the field loses focus
  const makeBlurHandler = (field) => (e) => {
    const trimmed = (e.target.value || '').trim();
    if (trimmed !== e.target.value) {
      setUpdateFormData((prev) => ({ ...prev, [field]: trimmed }));
    }
  };

  // Trims pasted text, then inserts it at the cursor position
  const makePasteHandler = (field) => (e) => {
    e.preventDefault();
    const { selectionStart, selectionEnd, value } = e.target;
    const pasted = e.clipboardData.getData('text').trim();
    const next = (value.slice(0, selectionStart) + pasted + value.slice(selectionEnd)).trim();
    setUpdateFormData((prev) => ({ ...prev, [field]: next }));
  };

  const handleSignatureImage = async (e) => {
    const imageFile = e.target?.files[0];

    if (imageFile) {
      const maxSizeInBytes = 1024 * 1024; // 1MB
      const currentSizeInBytes = imageFile.size;

      if (currentSizeInBytes <= maxSizeInBytes) {
        // If the image is already smaller than 1MB, no need to compress
        setUpdateFormData({ ...updateFormData, [e.target.name]: imageFile });
      } else {
        // Image needs compression
        try {
          const options = {
            maxSizeMB: 1,
            useWebWorker: true,
          };
          const compressedImage = await imageCompression(imageFile, options);
          setUpdateFormData({ ...updateFormData, [e.target.name]: compressedImage });
        } catch (error) {
          setShowMessage({
            message: 'Error compressing image. Please try again.',
            messageType: 'error',
          });
        }
      }
    }
  };

  // const handleUpdateData = async () => {
  //   setLoading(true);
  //   try {
  //     const {
  //       name,
  //       phone,
  //       address,
  //       gstNumber,
  //       confirmGstNumber,
  //       perRewardPointsPrice,
  //       websiteLink,
  //       signature, // binary (File) for signature
  //     } = updateFormData;

  //     const formDataUpdated = new FormData();
  //     formDataUpdated.append('name', name);
  //     formDataUpdated.append('phone', phone);
  //     formDataUpdated.append('address', address);
  //     formDataUpdated.append('gstNumber', gstNumber.trim() === '' ? '' : gstNumber);
  //     formDataUpdated.append('confirmGstNumber', confirmGstNumber.trim() === '' ? '' : confirmGstNumber);
  //     formDataUpdated.append('perRewardPointsPrice', perRewardPointsPrice);
  //     formDataUpdated.append('websiteLink', websiteLink);

  //     if (signature) {
  //       formDataUpdated.append('signature', signature); // binary file
  //     }
  //     console.log(formDataUpdated, "newwwwwww")
  //     const res = await updateOwnerProfile(data._id, formDataUpdated);
  //     if (res.statusCode === 200) {
  //       setShowMessage({
  //         message: res.message || 'Successfully Updated Profile',
  //         messageType: 'success',
  //       });
  //       setUpdateFormData(initialUpdateData);
  //       refreshData(data._id);
  //       setUpdateType(null);
  //       setOpenDialogue(false);
  //     } else {
  //       setShowMessage({
  //         message: res.message || 'Update Profile Failed',
  //         messageType: 'error',
  //       });
  //     }
  //   } catch (error) {
  //     setShowMessage({
  //       message: error.message || 'Something went wrong',
  //       messageType: 'error',
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const handleUpdateData = async () => {
  //   setLoading(true);
  const handleUpdateData = async () => {
  const gst = updateFormData.gstNumber;
  if (gst) {
    const gstProblem =
      getGstFormatError(gst) ||
      getGstLengthError(gst) ||
      gstError ||
      (updateFormData.confirmGstNumber !== gst ? 'GST numbers do not match' : '');

    if (gstProblem) {
      setGstTouched(true);
      setShowMessage({ message: gstProblem, messageType: 'error' });
      return;
    }
  }
  setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(updateFormData).forEach(([key, value]) => {
        if (value !== null && key !== 'signature') {
          let clean = typeof value === 'string' ? value.trim() : value;
          if (key === 'phone') clean = cleanPhone(clean);
          formData.append(key, clean);
        }
      });

      if (updateFormData.signature) {
        formData.append('signature', updateFormData.signature);
      }

      const res = await updateOwnerProfile(data._id, formData);
      if (res.statusCode === 200) {
        setShowMessage({
          message: 'Profile updated successfully',
          messageType: 'success',
        });
        refreshData(data._id);
        setOpenDialogue(false);
      }
    } catch (error) {
      setShowMessage({
        message: error.message || 'Update failed',
        messageType: 'error',
      });
    } finally {
      setLoading(false);
    }
  };




  const handleImage = async (e) => {
    const imageFile = e.target?.files[0];

    if (imageFile) {
      const maxSizeInBytes = 1024 * 1024; // 1MB
      const currentSizeInBytes = imageFile.size;

      if (currentSizeInBytes <= maxSizeInBytes) {
        setLogo(imageFile);
      } else {
        try {
          const options = {
            maxSizeMB: 1,
            useWebWorker: true,
          };
          const compressedImage = await imageCompression(imageFile, options);
          setLogo(compressedImage);
        } catch (error) {
          setShowMessage({
            message: 'Error compressing image. Please try again.',
            messageType: 'error',
          });
        }
      }
    }
  };

  const handleUpdateDisplayImg = async () => {
    const formDataUpdated = new FormData();
    formDataUpdated.append('logo', logo);

    try {
      setLoading(true);
      const res = await updateOwnerProfileImage(data._id, formDataUpdated);
      if (res.statusCode === 200) {
        setShowMessage({
          message: res.message || 'Successfully Updated Profile Image',
          messageType: 'success',
        });
        refreshData(data._id);
        setUpdateType(null);
        setLogo('');
        setOpenDialogue(false);
      } else {
        setShowMessage({
          message: res.message || 'Update Profile Image Failed',
          messageType: 'error',
        });
      }
    } catch (error) {
      setShowMessage({ message: error.message || 'Something went wrong', messageType: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.container}>
      <Box display={'flex'} alignItems={'center'} gap={'20px'}>
        <Avatar
          src={data?.logo ? data?.logo?.url : ProfileImg}
          alt={data?.logo ? data?.logo?.url : ProfileImg}
          sx={{
            width: 45,
            height: 45,
            cursor: 'pointer',
          }}
          onClick={() => {
            handleOpenDialogue('UPDATEAVATAR');
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            handleOpenDialogue('UPDATEPROFILE');
          }}
        >
          Update Profile
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setOpenTemplateDialog(true);
            // Initialize with current template when opening
            setUpdateFormData(prev => ({
              ...prev,
              selectedBillTemplate: selectedTemplate || 1
            }));
          }}
        >
          Change Bill Template
        </Button>

        {/* Owner Details Dialog */}
        {/* //actual update owner details dialog */}
        <Dialog open={openDialogue} onClose={handleCloseDialogue} maxWidth="md">
          <DialogTitle>Owner Details</DialogTitle>
          <DialogContent>
            <DialogContentText>Here you can update Owner details.</DialogContentText>
            {updateType === 'UPDATEPROFILE' ? (
              <Box style={{ marginTop: '20px' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                   <div onBlur={makeBlurHandler('name')} onPaste={makePasteHandler('name')}>
                    <TextInputs
                      title="Name"
                      name="name"
                      value={updateFormData.name}
                      handleChange={handleChange}
                      type="text"
                      required={false}
                    />
                  </div>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextInputs
                      title="Contact Number"
                      name="phone"
                      value={updateFormData.phone}
                      handleChange={handleChange}
                      type="text"
                      required={false}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextInputs
                      title="Address"
                      name="address"
                      value={updateFormData.address}
                      handleChange={handleChange}
                      type="text"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '6px' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextInputs
                          title="Gst Number"
                          name="gstNumber"
                          value={updateFormData.gstNumber}
                          handleChange={handleChange}
                          type="text"
                          required={false}
                        />
                      </Box>
                      <Tooltip title="Open GST detail" arrow>
                        <span>
                          <Button
                            variant="contained"
                            sx={{ background: '#6174DD', minWidth: '40px', height: '3.3rem', p: 0 }}
                            disabled={gstLoading || updateFormData.gstNumber?.length !== 15 || !!gstError}
                            onClick={async () => {
                              setOpenGstDialog(true);
                              if (gstDetail?.gstin !== updateFormData.gstNumber) {
                                await fetchGstDetail(updateFormData.gstNumber);
                              }
                            }}
                          >
                            {gstLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <InfoOutlinedIcon />}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                    <Box sx={{ minHeight: '18px', fontSize: '12px', mt: 0.5 }}>
                      {gstError ? (
                        <span style={{ color: 'red' }}>{gstError}</span>
                      ) : gstTouched && getGstLengthError(updateFormData.gstNumber) ? (
                        <span style={{ color: 'red' }}>{getGstLengthError(updateFormData.gstNumber)}</span>
                      ) : gstDetail ? (
                        <span style={{ color: 'green' }}>GST verified ✓</span>
                      ) : null}
                      {!!updateFormData.gstNumber && (
                        <span
                          style={{
                            float: 'right',
                            color: updateFormData.gstNumber.length === 15 ? 'green' : '#d32f2f',
                          }}
                        >
                          {updateFormData.gstNumber.length}/15
                        </span>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextInputs
                      title="Confirm Gst Number"
                      name="confirmGstNumber"
                      value={updateFormData.confirmGstNumber}
                      handleChange={handleChange}
                      type="text"
                      required={false}
                    />
                    <Box sx={{ minHeight: '18px', fontSize: '12px', mt: 0.5, color: 'red' }}>
                      {confirmGstError}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <label style={{ padding: '2px', display: 'block', marginBottom: '0' }}>
                      Per Reward Point Value
                    </label>
                    <InputSelect
                      name="perRewardPointsPrice"
                      value={updateFormData.perRewardPointsPrice}
                      onChange={handleChange}
                      options={[
                        { value: 0.05, label: '0.05' },
                        { value: 0.10, label: '0.10' },
                        { value: 0.15, label: '0.15' },
                        { value: 0.20, label: '0.20' },
                      ]}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextInputs
                      title="Website Link"
                      name="websiteLink"
                      value={updateFormData.websiteLink}
                      handleChange={handleChange}
                      type="text"
                      required={false}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                      <CustomTimeSlotSelector
                        title="Salon Opening Time"
                        name="salonOpeningTime"
                        value={updateFormData.salonOpeningTime}
                        handleChange={handleChange}
                        startTime={0}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <CustomTimeSlotSelector
                        title="Salon Closing Time"
                        name="salonClosingTime"
                        value={updateFormData.salonClosingTime}
                        handleChange={handleChange}
                        startTime={0}
                        minTime={updateFormData.salonOpeningTime}
                      />
                    </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={data?.signature ? data?.signature?.url : ProfileImg}
                          alt="Signature"
                          sx={{
                            width: 45,
                            height: 45,
                            objectFit: 'contain',
                            border: '1px solid #cfcfcf',
                            backgroundColor: '#f5f5f5',
                            marginTop: '25px'
                          }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <TextInputs
                            required={false}
                            title={'Signature (max size: 1MB)'}
                            name={'signature'}
                            value={updateFormData?.signature || ''}
                            handleChange={handleSignatureImage}
                            type={'file'}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box style={{ marginTop: '20px' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={data?.logo ? data?.logo?.url : ProfileImg}
                        alt="Logo"
                        sx={{
                          width: 45,
                          height: 45,
                          border: '1px solid #cfcfcf',
                          backgroundColor: '#f5f5f5'
                        }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <TextInputs
                          title={'Update Logo (max size: 1MB)'}
                          name={'logo'}
                          handleChange={handleImage}
                          type={'file'}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {loading ? (
              <ShowLoader />
            ) : (
              <>
                <Button onClick={handleCloseDialogue}>Cancel</Button>
                {updateType === 'UPDATEAVATAR' ? (
                  <Button onClick={handleUpdateDisplayImg}>Update</Button>
                ) : (
                  <Button onClick={handleUpdateData} style={{ backgroundColor: '#6174DD', color: 'white' }}>Update</Button>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>

      {/* <Dialog 
        open={openTemplateDialog} 
        onClose={() => setOpenTemplateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Bill Template</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Current selected template: {templates.find(t => t.id === updateFormData.selectedBillTemplate)?.name}
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {templates.map((template) => (
              <Grid item xs={12} md={4} key={template.id}>
                <Box
                  sx={{
                    border: updateFormData.selectedBillTemplate === template.id ? '2px solid #6174DD' : '1px solid #ddd',
                    borderRadius: '8px',
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <img 
                    src={template.image} 
                    alt={template.name} 
                    style={{ 
                      width: '100%', 
                      height: 'auto',
                      borderRadius: '4px'
                    }} 
                  />
                  <Typography variant="h6" align="center" sx={{ mt: 2 }}>
                    {template.name}
                  </Typography>
                  {updateFormData.selectedBillTemplate === template.id && (
                    <Typography variant="body2" align="center" color="primary">
                      Selected
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTemplateDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              // First update the profile data with the selected template
              handleUpdateData();
              setOpenTemplateDialog(false);
            }}
            variant="contained"
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog> */}
      <Dialog
        open={openTemplateDialog}
        onClose={() => setOpenTemplateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Bill Template</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
            Current selected template: <span style={{ color: '#6174DD' }}>{templates.find(t => t.id === updateFormData.selectedBillTemplate)?.name}</span>
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {templates.map((template) => (
              <Grid item xs={12} md={4} key={template.id}>
                <Box
                  sx={{
                    border: updateFormData.selectedBillTemplate === template.id
                      ? '2px solid #6174DD'
                      : '1px solid #e0e0e0',
                    borderRadius: '8px',
                    p: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundColor: updateFormData.selectedBillTemplate === template.id
                      ? '#f5f7ff'
                      : '#ffffff',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
                      borderColor: '#6174DD'
                    }
                  }}
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: '200px',
                      overflow: 'hidden',
                      borderRadius: '4px',
                      mb: 2
                    }}
                  >
                    <img
                      src={template.image}
                      alt={template.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center'
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    align="center"
                    sx={{
                      mt: 1,
                      fontWeight: 'medium',
                      color: updateFormData.selectedBillTemplate === template.id
                        ? '#6174DD'
                        : 'text.primary'
                    }}
                  >
                    {template.name}
                  </Typography>
                  {updateFormData.selectedBillTemplate === template.id && (
                    <Box
                      sx={{
                        mt: 1,
                        textAlign: 'center',
                        color: '#6174DD',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <span style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#6174DD',
                        marginRight: '8px'
                      }}></span>
                      Selected
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setOpenTemplateDialog(false)}
            variant="outlined"
            sx={{
              color: '#6174DD',
              borderColor: '#6174DD',
              '&:hover': {
                backgroundColor: '#f5f7ff',
                borderColor: '#6174DD'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleUpdateData();
              setOpenTemplateDialog(false);
            }}
            variant="contained"
            sx={{
              backgroundColor: '#6174DD',
              '&:hover': {
                backgroundColor: '#4a5bb5'
              }
            }}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openGstDialog}
        onClose={() => setOpenGstDialog(false)}
        PaperProps={{ style: { width: 520, maxWidth: '95%', padding: 8 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          GST Details
          <IconButton onClick={() => setOpenGstDialog(false)} sx={{ color: 'red' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {gstLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#6174DD' }} />
            </Box>
          ) : gstDetail ? (
            <Box>
              <Box sx={{ background: '#f4f6fd', border: '1px solid #e8ebf9', borderRadius: '8px', p: 2, mb: 2 }}>
                <Typography sx={{ fontWeight: 'bold', color: '#6174DD', fontSize: '18px' }}>
                  {gstDetail.legalName}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#666' }}>{gstDetail.gstin}</Typography>
                <span
                  style={{
                    background: gstDetail.status === 'Active' ? '#2e7d32' : '#d32f2f',
                    color: '#fff',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    display: 'inline-block',
                    marginTop: 6,
                  }}
                >
                  {gstDetail.status}
                </span>
              </Box>

              {[
                ['Trade Name', gstDetail.tradeName],
                ['Taxpayer Type', gstDetail.taxpayerType],
                ['Registration Date', gstDetail.registrationDate],
                ['Cancellation Date', gstDetail.cancellationDate],
                ['State Code', gstDetail.stateCode],
                ['Address', gstDetail.address],
                ['City', gstDetail.city],
                ['Pincode', gstDetail.pincode],
                ['Block Status', gstDetail.blockStatus],
              ].map(([label, val]) => (
                <Box
                  key={label}
                  sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: '1px solid #eee', gap: 2 }}
                >
                  <Typography sx={{ fontSize: '14px', color: '#666', minWidth: 130 }}>{label}</Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, textAlign: 'right' }}>
                    {val || '-'}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: 'error.main', textAlign: 'center', py: 3 }}>
              {gstError || 'GST detail not available'}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            sx={{ background: '#6174DD' }}
            disabled={!gstDetail}
            onClick={() => {
              setUpdateFormData((prev) => ({
                ...prev,
                name: gstDetail.legalName || prev.name,
                address: gstDetail.address || prev.address,
              }));
              setOpenGstDialog(false);
            }}
          >
            Use Name &amp; Address
          </Button>
          <Button onClick={() => setOpenGstDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default OwnerProfile;

